import { MiraSectionDocument, MiraTrendGrain, MiraTrendPoint, MiraTrendSeries } from "@/data/models/Mira";
import {
  COLOR_MIRA_SERIES_1,
  COLOR_MIRA_SERIES_1_DARK,
  COLOR_MIRA_SERIES_2,
  COLOR_MIRA_SERIES_2_DARK,
  COLOR_MIRA_SERIES_3,
  COLOR_MIRA_SERIES_3_DARK,
  COLOR_MIRA_SERIES_4,
  COLOR_MIRA_SERIES_4_DARK,
  COLOR_MIRA_SERIES_5,
  COLOR_MIRA_SERIES_5_DARK,
  COLOR_MIRA_SERIES_6,
  COLOR_MIRA_SERIES_6_DARK,
  COLOR_MIRA_SERIES_7,
  COLOR_MIRA_SERIES_7_DARK,
  COLOR_MIRA_SERIES_8,
  COLOR_MIRA_SERIES_8_DARK,
  COLOR_MIRA_SERIES_REST,
  COLOR_MIRA_SERIES_REST_DARK,
  COLOR_MIRA_LEVEL_1,
  COLOR_MIRA_LEVEL_1_DARK,
  COLOR_MIRA_LEVEL_2,
  COLOR_MIRA_LEVEL_2_DARK,
  COLOR_MIRA_LEVEL_3,
  COLOR_MIRA_LEVEL_3_DARK,
  COLOR_MIRA_LEVEL_4,
  COLOR_MIRA_LEVEL_4_DARK,
  COLOR_MIRA_LEVEL_5,
  COLOR_MIRA_LEVEL_5_DARK,
} from "@/utils/colors";
import { MiraViewContext } from "@/utils/MiraDataQualityView";
import { caseCountIsValue } from "@/utils/MiraCaseUnits";
import { MIRA_TOTAL_METRICS, buildTrend, isTotalMetric, trendSeriesPoints } from "@/utils/MiraTrendView";
import { MiraMetricDirection, metricDirection } from "@/utils/MiraMetricDirection";

/**
 * The stacked Verlauf card's view model: a metric's timeline drawn as one stacked bar per period, each
 * bar split into labelled, coloured parts that sum to the bar's total. Two things stack, and only two
 * (Hans, 2026-10-05, "sums stack, averages never"):
 *
 * - a **segment figure** — Projektablauf (MIRA-PHA-011) and Leistungen (MIRA-PRJ-017) — whose parts come
 *   from the point's own {@link MiraTrendPoint.segments}; built by {@link buildStackedTrend};
 * - a **total** (the four {@link MIRA_TOTAL_METRICS}) compared across members or branches; built by
 *   {@link buildComparisonBars}, which refuses anything that is not a total. These are **grouped** bars,
 *   one per entity side by side per period (a double bar graph), not a stack: a comparison sets peers
 *   next to each other (Taimoor, 2026-10-05, after the browser review). Only the parts of one whole stack.
 * - an **average / share** compared across members or branches; built by
 *   {@link buildAverageComparisonBars} (Taimoor, 2026-10-05: averages and shares get grouped bars too,
 *   like totals, but each bar is the mean / share only, never a sum). It ranks the entities by their
 *   **number of cases**, not their value, and hides any bar backed by fewer than
 *   {@link MIRA_COMPARISON_BAR_FLOOR} cases (the same floor the tiles use for a subpopulation), so a bar
 *   can never be read off a handful of projects.
 *
 * Pure, like the other Mira view models. It reuses {@link buildTrend} for the per-point scaffolding
 * (labels, gaps, the partial year, the low-count mark, the basis), so both cards and the line card mark a
 * period the same way. The parts of a segment figure carry their colour in from the screen; a comparison
 * bar takes its entity's stable colour from the shared map the caller passes (built once by
 * {@link buildStableEntityColors}), so a company / branch keeps its colour across every tile and screen.
 * Both builders return the same {@link MiraStackedTrendView}; the card draws it stacked or grouped by its
 * mode.
 */

/**
 * Up to how many members / branches the comparison timeline names each one (Taimoor, #2610112, 2026-10-29:
 * as many as the distinct entity colours). Above it the timeline draws the five levels instead
 * ({@link buildLevelBars}) and the ranking card below names everyone.
 */
export const MIRA_COMPARISON_NAME_LIMIT = 8;

/** Whether a comparison of this many entities draws the five levels rather than one bar per entity. */
export function comparisonUsesLevels(entityCount: number): boolean {
  return entityCount > MIRA_COMPARISON_NAME_LIMIT;
}

/**
 * The fewest cases an average / share comparison bar may rest on; a bar backed by fewer is hidden (a gap).
 * The same floor a tile uses for a subpopulation (Taimoor, 2026-10-05): a mean of a handful of projects is
 * noise, so it is not drawn and the card notes that some bars are hidden.
 */
export const MIRA_COMPARISON_BAR_FLOOR = 10;

/** The two segment figures whose parts stack into one bar; never compared across members / branches. */
export const MIRA_SEGMENT_METRICS = ["MIRA-PHA-011", "MIRA-PRJ-017"];

/** A part of every bar, aligned to {@link MiraStackedTrendView.points} (one value per point, `null` a gap). */
export interface MiraStackedSeries {
  /** Stable key: a stage / service id, or a client / branch id. */
  key: string;
  label: string;
  color: string;
  values: (number | null)[];
  /**
   * Comparison of an average / share only: the cases behind each drawn bar, aligned to the points, for the
   * tooltip's "· 412 Projekte" line (`null` where no bar is drawn), in the figure's own unit
   * (`MiraCaseUnits.ts`). Absent for a segment figure and for Projekte, whose case count is the value.
   */
  basis?: (number | null)[];
  /**
   * Levels only: the entity behind the Best / Worst (or Höchster / Niedrigster) bar at each point, for the
   * tooltip; `null` where no bar is drawn. Absent on the three middle levels, which are no single entity.
   */
  entityLabels?: (string | null)[];
}

/** One bar on the stacked timeline: the whole-bar scaffolding; the parts live in the series. */
export interface MiraStackedPoint {
  key: string;
  from: string;
  toExclusive: string;
  label: string;
  /** Sum of the bar's parts, or `null` for a gap (no bar drawn). */
  total: number | null;
  totalText: string | null;
  basis: number;
  /** The current year to date, marked on the whole bar. */
  partial: boolean;
  belowFloor: boolean;
}

/** A metric's stacked (segment) or grouped (comparison) timeline at one grain. */
export interface MiraStackedTrendView {
  points: MiraStackedPoint[];
  series: MiraStackedSeries[];
  /** Whether any bar carries a value: an all-gap grain shows the empty note instead of a chart. */
  hasData: boolean;
  /**
   * Comparison only: how many entities rank below the shown top ones (not drawn), for the "{n} weitere in
   * der Rangliste darunter" caption. 0 or absent for a segment figure and where everyone is shown.
   */
  restCount?: number;
  /**
   * Average / share comparison only: at least one bar was hidden because it rested on fewer than
   * {@link MIRA_COMPARISON_BAR_FLOOR} cases, so the card shows the "Balken mit weniger als N Fällen sind
   * ausgeblendet" note. Absent / false for a total comparison and a segment figure.
   */
  belowBarFloor?: boolean;
  /**
   * The comparison draws the five levels across the entities ({@link buildLevelBars}), not one bar per
   * entity: the card then names no entity in its legend and explains the levels instead.
   */
  levels?: boolean;
}

/** A part the screen supplies for a segment figure: an id, its label and its already-themed colour. */
export interface MiraStackedSegmentDef {
  key: string;
  label: string;
  color: string;
  /**
   * Leave the part out of the chart and legend when it has nothing at any drawn point. Leistungen sets it
   * so a company that never performs a service (a GZ-only client such as BELFOR or svt has no Leckortung,
   * Schadenaufnahme or Wiederherstellung) is not shown a row of zeros for it (Taimoor, 2026-10-07).
   */
  hideWhenEmpty?: boolean;
}

/** How many entities get a distinct categorical colour; every other entity shares the neutral grey. */
export const MIRA_ENTITY_COLOR_SLOTS = 8;

/** An entity to colour, with the size that orders it: its projects in the chosen period (MIRA-PRJ-001). */
export interface MiraSizedEntity {
  id: string;
  label: string;
  /** The entity's size in the chosen period; `null` where the size source carries none (sorts last). */
  size: number | null;
}

/**
 * The one stable colour per entity for the whole comparison, keyed by entity id, so every card and every
 * Mira screen paints a company / branch the same while the comparison and period are unchanged (Taimoor,
 * 2026-10-06). The entities are ordered once by size (their projects in the chosen period, MIRA-PRJ-001),
 * largest first, ties by name; the largest {@link MIRA_ENTITY_COLOR_SLOTS} take the categorical palette
 * 1..8, every other entity takes {@link COLOR_MIRA_SERIES_REST} (a neutral grey). An entity whose size
 * source is missing sorts after the others by name, so its colour is deterministic too. Pure, theme-aware.
 */
export function buildStableEntityColors(entities: MiraSizedEntity[], dark: boolean): Record<string, string> {
  const palette = dark
    ? [
        COLOR_MIRA_SERIES_1_DARK,
        COLOR_MIRA_SERIES_2_DARK,
        COLOR_MIRA_SERIES_3_DARK,
        COLOR_MIRA_SERIES_4_DARK,
        COLOR_MIRA_SERIES_5_DARK,
        COLOR_MIRA_SERIES_6_DARK,
        COLOR_MIRA_SERIES_7_DARK,
        COLOR_MIRA_SERIES_8_DARK,
      ]
    : [
        COLOR_MIRA_SERIES_1,
        COLOR_MIRA_SERIES_2,
        COLOR_MIRA_SERIES_3,
        COLOR_MIRA_SERIES_4,
        COLOR_MIRA_SERIES_5,
        COLOR_MIRA_SERIES_6,
        COLOR_MIRA_SERIES_7,
        COLOR_MIRA_SERIES_8,
      ];
  const rest = dark ? COLOR_MIRA_SERIES_REST_DARK : COLOR_MIRA_SERIES_REST;

  const ordered = [...entities].sort((a, b) => {
    const hasA = a.size !== null && a.size !== undefined;
    const hasB = b.size !== null && b.size !== undefined;
    if (hasA && hasB && a.size !== b.size) {
      return (b.size as number) - (a.size as number);
    }
    // An entity with a known size always outranks one without; otherwise fall back to the name.
    if (hasA !== hasB) {
      return hasA ? -1 : 1;
    }
    return a.label.localeCompare(b.label);
  });

  const colors: Record<string, string> = {};
  ordered.forEach((entity, index) => {
    colors[entity.id] = index < palette.length ? palette[index] : rest;
  });
  return colors;
}

/** The stable colour an entity takes, from the shared map, falling back to the neutral grey rest colour. */
function entityColor(entityId: string, entityColors: Record<string, string>, dark: boolean): string {
  return entityColors[entityId] ?? (dark ? COLOR_MIRA_SERIES_REST_DARK : COLOR_MIRA_SERIES_REST);
}

/** The per-point scaffolding, shared by both builders: {@link buildTrend}'s points as {@link MiraStackedPoint}s. */
function scaffold(base: ReturnType<typeof buildTrend>["points"]): MiraStackedPoint[] {
  return base.map((point) => ({
    key: point.key,
    from: point.from,
    toExclusive: point.toExclusive,
    label: point.label,
    total: point.value,
    totalText: point.valueText,
    basis: point.basis,
    partial: point.partial,
    belowFloor: point.belowFloor,
  }));
}

/**
 * The stacked timeline of a **segment figure** (MIRA-PHA-011, MIRA-PRJ-017): one bar per period, split
 * into the parts the caller names, read from each point's {@link MiraTrendPoint.segments}. A part a point
 * has nothing for is 0; a gap point (no value, or below the floor) draws no bar, so every part is a gap
 * there too. A part marked {@link MiraStackedSegmentDef.hideWhenEmpty} is dropped when it is 0 at every
 * drawn point. `hasData` is false where the grain carries no segments at all (an older document) or no part
 * is left, so the card hides rather than drawing empty bars.
 *
 * @param segments the parts in display (stack) order, each with its label and themed colour.
 * @param formatValue the screen's formatter, so the bar total reads in the figure's unit.
 */
export function buildStackedTrend(
  trends: MiraSectionDocument["trends"] | undefined,
  metricId: string,
  grain: MiraTrendGrain,
  segments: MiraStackedSegmentDef[],
  formatValue: (value: number) => string,
  ctx: MiraViewContext
): MiraStackedTrendView {
  const base = buildTrend(trends, metricId, grain, formatValue, ctx);
  const raw: MiraTrendPoint[] = trendSeriesPoints(trends, metricId, grain);
  const points = scaffold(base.points);

  const series: MiraStackedSeries[] = segments
    .map((segment) => ({
      segment,
      part: {
        key: segment.key,
        label: segment.label,
        color: segment.color,
        values: points.map((point, index) =>
          point.total === null ? null : raw[index]?.segments?.[segment.key] ?? 0
        ),
      },
    }))
    .filter(({ segment, part }) => !segment.hideWhenEmpty || part.values.some((value) => (value ?? 0) > 0))
    .map(({ part }) => part);

  // The bar total is the sum of its parts, so the stack height and its total always agree. This matters
  // for Leistungen, where a project counts in several services and the parts exceed the distinct value.
  points.forEach((point, index) => {
    if (point.total === null) {
      point.totalText = null;
      return;
    }
    const total = series.reduce((sum, part) => sum + (part.values[index] ?? 0), 0);
    point.total = total;
    point.totalText = formatValue(total);
  });

  const anySegments = raw.some((point) => point.segments !== undefined && point.segments !== null);
  return { points, series, hasData: anySegments && series.length > 0 && points.some((point) => point.total !== null) };
}

/** A member or a branch the comparison stacks: an id, a display name, and its own timeline for the metric. */
export interface MiraStackEntity {
  id: string;
  label: string;
  /** The entity's trend series for the metric (its own document's, or the branch document's). */
  series: MiraTrendSeries | undefined;
  /**
   * A bar drawn before the others in the order given, never ranked, never counted in the five levels:
   * the whole the others are segments of ("Trocknungsdauer gesamt", Hans, #2610137), and the scope above it
   * (the Markt, a member's Gruppe) where both comparisons are on (2026-11-02). Averages and shares only.
   */
  reference?: boolean;
}

/** The numeric contribution of an entity at one grain point key (absent or a real gap counts as 0). */
function entityValueAt(entity: MiraStackEntity, grain: MiraTrendGrain, key: string): number {
  const point = (entity.series?.[grain] ?? []).find((candidate) => candidate.key === key);
  const value = point?.value;
  return value === null || value === undefined || point?.belowFloor === true ? 0 : value;
}

/** The entity's own trend point at one grain point key, or `undefined` when it carries none (its own cases, value, flags). */
function entityPointAt(entity: MiraStackEntity, grain: MiraTrendGrain, key: string): MiraTrendPoint | undefined {
  return (entity.series?.[grain] ?? []).find((candidate) => candidate.key === key);
}

/** The leading entities (the bar upwards, the gesamt bar), in their order, and the segments, apart. */
function splitReference(entities: MiraStackEntity[]): { references: MiraStackEntity[]; segments: MiraStackEntity[] } {
  return {
    references: entities.filter((entity) => entity.reference),
    segments: entities.filter((entity) => !entity.reference),
  };
}

/**
 * One entity's average / share bars: its mean per period, a gap where the reference period is one, or where
 * the value rests on fewer than {@link MIRA_COMPARISON_BAR_FLOOR} cases (`hidden` then says a real value
 * was held back, for the card's note).
 */
function averageBars(
  entity: MiraStackEntity,
  points: MiraStackedPoint[],
  grain: MiraTrendGrain,
  color: string
): { series: MiraStackedSeries; hidden: boolean } {
  const values: (number | null)[] = [];
  const basis: (number | null)[] = [];
  let hidden = false;
  points.forEach((point) => {
    const cell = entityPointAt(entity, grain, point.key);
    if (point.total === null) {
      // The reference period is a gap: no bars are drawn there for anyone.
      values.push(null);
      basis.push(null);
      return;
    }
    const value = cell?.value;
    const hasValue = value !== null && value !== undefined;
    const enoughCases = (cell?.basis ?? 0) >= MIRA_COMPARISON_BAR_FLOOR && cell?.belowFloor !== true;
    if (hasValue && enoughCases) {
      values.push(value as number);
      basis.push(cell?.basis ?? 0);
    } else {
      // A real value held back only by the floor is "hidden"; a period the entity simply had nothing in
      // is not. The distinction drives the "some bars hidden" note.
      if (hasValue && !enoughCases) {
        hidden = true;
      }
      values.push(null);
      basis.push(null);
    }
  });
  return { series: { key: entity.id, label: entity.label, color, values, basis }, hidden };
}

/** The entity's total cases (sum of `basis`) over the whole grain, for ranking an average / share comparison. */
function entityCasesOverGrain(entity: MiraStackEntity, grain: MiraTrendGrain): number {
  return (entity.series?.[grain] ?? []).reduce((sum, point) => sum + (point.basis ?? 0), 0);
}

/** The five levels in display order, each with the quantile it reads. */
interface LevelDef {
  key: string;
  label: string;
  q: number;
}

/**
 * The entity at a quantile by nearest rank (Hans, #2610137: "show the clients for each bar"): an actual
 * entity's value, never one between two, so every bar has one client behind it. Of entities tied on that
 * value, the first by name. `counted` is sorted by value ascending, then by name.
 */
function nearestRank(counted: Array<{ label: string; value: number }>, q: number): { label: string; value: number } {
  const value = counted[Math.round(q * (counted.length - 1))].value;
  return counted.find((entry) => entry.value === value) ?? counted[0];
}

/**
 * The five levels for a direction, best (or highest) first; {@link buildLevelBars} draws them worst first
 * (#2610137). Each middle level is labelled by its
 * quantile of the values, so "25 %" always means the same mark; only its position moves: next to Best
 * where lower is better (a duration, Hans's example), next to Worst where higher is better.
 */
function levelDefs(direction: MiraMetricDirection, ctx: MiraViewContext): LevelDef[] {
  const percent = (value: number) => ctx.t("mira.format.percent", [ctx.formatNumber(value)]);
  const top = direction === null ? ctx.t("mira.levels.highest") : ctx.t("mira.levels.best");
  const bottom = direction === null ? ctx.t("mira.levels.lowest") : ctx.t("mira.levels.worst");
  // Ascending by value where lower is better, descending otherwise.
  const quantiles = direction === "LOWER" ? [0, 0.25, 0.5, 0.75, 1] : [1, 0.75, 0.5, 0.25, 0];
  return quantiles.map((q, index) => ({
    key: ["top", "upper", "median", "lower", "bottom"][index],
    label: index === 0 ? top : index === 4 ? bottom : percent(q * 100),
    q,
  }));
}

/** The level colours, strongest first, theme-aware. */
function levelColors(dark: boolean): string[] {
  return dark
    ? [COLOR_MIRA_LEVEL_1_DARK, COLOR_MIRA_LEVEL_2_DARK, COLOR_MIRA_LEVEL_3_DARK, COLOR_MIRA_LEVEL_4_DARK, COLOR_MIRA_LEVEL_5_DARK]
    : [COLOR_MIRA_LEVEL_1, COLOR_MIRA_LEVEL_2, COLOR_MIRA_LEVEL_3, COLOR_MIRA_LEVEL_4, COLOR_MIRA_LEVEL_5];
}

/**
 * The comparison timeline of more than {@link MIRA_COMPARISON_NAME_LIMIT} members or branches (Hans,
 * #2610112 option B, decided by Taimoor on 2026-10-29): instead of one bar per entity, five bars per
 * period, computed across the entities with a value **in that period**: Best, the 25 %, 50 % and 75 %
 * marks and Worst (Höchster / Niedrigster for a figure with no better direction, {@link metricDirection}).
 * The marks are quantiles across the entities, each entity counted once, never a share of the value, taken
 * by nearest rank ({@link nearestRank}, #2610137): each bar is one entity's own value, named in
 * `entityLabels` for every level.
 *
 * Which entities count in a period follows the bar rules of the two builders: for a total, every entity
 * with a value there (a real 0 included); for an average or share, only a value resting on at least
 * {@link MIRA_COMPARISON_BAR_FLOOR} cases, any other one leaving out and setting
 * {@link MiraStackedTrendView.belowBarFloor}. A period without one such entity is a gap. Every bar carries
 * the entity behind it for the tooltip and the legend (the first by name on a tie).
 * {@link MiraStackedPoint.total} stays the reference's own figure, as in the other comparison builders.
 *
 * **Worst on the left, best on the right** (Hans, #2610137: "right and top is better than left and
 * bottom"); each level keeps its colour, the strongest on Best. The reference entities (the bar upwards and
 * the gesamt bar of an average) are drawn first, in their order, outside the levels.
 */
export function buildLevelBars(
  metricId: string,
  grain: MiraTrendGrain,
  referenceTrends: MiraSectionDocument["trends"] | undefined,
  entities: MiraStackEntity[],
  formatValue: (value: number) => string,
  ctx: MiraViewContext,
  entityColors: Record<string, string> = {}
): MiraStackedTrendView {
  const base = buildTrend(referenceTrends, metricId, grain, formatValue, ctx);
  const points = scaffold(base.points);
  const total = isTotalMetric(metricId);
  const defs = levelDefs(metricDirection(metricId), ctx);
  const colors = levelColors(ctx.dark);
  const { references, segments } = splitReference(entities);
  let belowBarFloor = false;

  // Per point: the entities that count there, by value ascending (ties by name, so a named Best / Worst is
  // deterministic).
  const perPoint = points.map((point) => {
    if (point.total === null) {
      return [];
    }
    const counted: Array<{ label: string; value: number }> = [];
    segments.forEach((entity) => {
      const cell = entityPointAt(entity, grain, point.key);
      const value = cell?.value;
      if (value === null || value === undefined || cell?.belowFloor === true) {
        return;
      }
      if (!total && (cell?.basis ?? 0) < MIRA_COMPARISON_BAR_FLOOR) {
        belowBarFloor = true;
        return;
      }
      counted.push({ label: entity.label, value });
    });
    return counted.sort((a, b) => a.value - b.value || a.label.localeCompare(b.label));
  });

  const series: MiraStackedSeries[] = defs.map((def, index) => {
    const marks = perPoint.map((counted) => (counted.length === 0 ? null : nearestRank(counted, def.q)));
    return {
      key: def.key,
      label: def.label,
      color: colors[index],
      values: marks.map((mark) => mark?.value ?? null),
      entityLabels: marks.map((mark) => mark?.label ?? null),
    };
  });
  // Worst first, best last; the colours were taken best first, so each level keeps its own.
  series.reverse();

  if (!total) {
    const leading = references.map((reference) =>
      averageBars(reference, points, grain, entityColor(reference.id, entityColors, ctx.dark))
    );
    belowBarFloor = belowBarFloor || leading.some((bars) => bars.hidden);
    series.unshift(...leading.map((bars) => bars.series));
  }

  return {
    points,
    series,
    hasData: series.some((level) => level.values.some((value) => value !== null)),
    restCount: 0,
    belowBarFloor,
    levels: true,
  };
}

/**
 * The grouped-bar timeline of a **total** compared across members or branches: one bar per entity, side
 * by side within each period (a double bar graph), **not** stacked. Up to
 * {@link MIRA_COMPARISON_NAME_LIMIT} entities each get a bar, ranked by their sum over the whole grain
 * unless the caller keeps its own order; more than that draw the five levels instead ({@link buildLevelBars},
 * #2610112). Refuses anything that is not one of {@link MIRA_TOTAL_METRICS}, so an average is never
 * compared this way — the guard.
 *
 * Each bar is the entity's own value for the period; {@link MiraStackedPoint.total} stays the reference's
 * own figure (the group or company total), so the tooltip footer reads the whole, not the sum of the
 * drawn bars. The reference's own trend ({@link referenceTrends}) supplies the period scaffolding and
 * whether a period is a gap; a gap period draws no bars.
 *
 * @param keepOrder - draw the entities in the order given instead of ranking them (the Markt: own, je Mandant,
 *   je Projekt on every card, Taimoor 2026-10-27).
 * @returns the grouped view, or `null` when the metric is not a total (never compare an average this way).
 */
export function buildComparisonBars(
  metricId: string,
  grain: MiraTrendGrain,
  referenceTrends: MiraSectionDocument["trends"] | undefined,
  entities: MiraStackEntity[],
  formatValue: (value: number) => string,
  ctx: MiraViewContext,
  entityColors: Record<string, string>,
  keepOrder = false
): MiraStackedTrendView | null {
  if (!isTotalMetric(metricId)) {
    return null;
  }
  // Above the name limit the levels replace the named bars, whatever order the caller keeps.
  if (comparisonUsesLevels(entities.length)) {
    return buildLevelBars(metricId, grain, referenceTrends, entities, formatValue, ctx, entityColors);
  }

  const base = buildTrend(referenceTrends, metricId, grain, formatValue, ctx);
  const points = scaffold(base.points);

  // Rank the entities by their total over the whole grain, descending (ties by label); every one gets a bar
  // (at most the name limit, above which the levels were drawn). The colour comes from the shared stable map, so it follows the
  // entity across every tile and screen, never the rank within this one figure.
  const ranked = keepOrder
    ? entities
    : [...entities].sort((a, b) => {
        const totalA = points.reduce((sum, point) => sum + entityValueAt(a, grain, point.key), 0);
        const totalB = points.reduce((sum, point) => sum + entityValueAt(b, grain, point.key), 0);
        return totalB - totalA || a.label.localeCompare(b.label);
      });

  const top = ranked.slice(0, MIRA_COMPARISON_NAME_LIMIT);
  // Each bar names its cases too (S1), unless they are the value itself (Projekte).
  const withBasis = !caseCountIsValue(metricId);
  const series: MiraStackedSeries[] = top.map((entity) => ({
    key: entity.id,
    label: entity.label,
    color: entityColor(entity.id, entityColors, ctx.dark),
    // The bar is a gap where the reference period is a gap, 0 where the entity itself had nothing.
    values: points.map((point) => (point.total === null ? null : entityValueAt(entity, grain, point.key))),
    ...(withBasis
      ? { basis: points.map((point) => (point.total === null ? null : entityPointAt(entity, grain, point.key)?.basis ?? null)) }
      : {}),
  }));

  return {
    points,
    series,
    hasData: series.length > 0 && points.some((point) => point.total !== null),
    restCount: Math.max(0, ranked.length - top.length),
  };
}

/**
 * The grouped-bar timeline of an **average or share** compared across members or branches: one bar per
 * entity, side by side within each period, each bar the entity's own **mean / share** (never a sum, and
 * never a median or a band: Hans, 2026-10-05, "then we use only sum / mean values"). The shape mirrors
 * {@link buildComparisonBars}, with three differences decided by Taimoor on 2026-10-05:
 *
 * - **Without an order from the caller, the entities rank by their cases**, the sum of each entity's
 *   `basis` over the grain, **not** by the highest value. Above {@link MIRA_COMPARISON_NAME_LIMIT} entities
 *   the five levels are drawn instead ({@link buildLevelBars}, #2610112).
 * - **A bar is hidden below the floor.** A bar whose point rests on fewer than
 *   {@link MIRA_COMPARISON_BAR_FLOOR} cases, or whose point is `belowFloor` / carries no value, is a gap;
 *   {@link MiraStackedTrendView.belowBarFloor} is set when any drawable bar was hidden this way, so the card
 *   notes it. Each drawn bar carries its own case count in {@link MiraStackedSeries.basis} for the tooltip.
 * - **Quartal and Jahr only** (the card hides the Monat button for an average): a mean over a single month
 *   of one company is too thin to compare. The grain is still chosen by the caller; this builder is grain
 *   agnostic.
 *
 * As with {@link buildComparisonBars}, {@link MiraStackedPoint.total} stays the reference's own figure (the
 * group's / company's mean) so the tooltip footer reads the whole, not a bar. The caller passes the
 * reference's own trend for the period scaffolding and the gap periods. The reference entities (the bar
 * upwards and the gesamt bar, #2610137) are drawn first, under the same floor, and never count towards the
 * name limit.
 *
 * @param keepOrder - draw the entities in the order given instead of ranking them (the Markt).
 * @returns the grouped view, or `null` when the metric is a total (use {@link buildComparisonBars}) or a
 *   segment figure (never compared this way).
 */
export function buildAverageComparisonBars(
  metricId: string,
  grain: MiraTrendGrain,
  referenceTrends: MiraSectionDocument["trends"] | undefined,
  entities: MiraStackEntity[],
  formatValue: (value: number) => string,
  ctx: MiraViewContext,
  entityColors: Record<string, string>,
  keepOrder = false
): MiraStackedTrendView | null {
  // A total has its own builder (its bars are sums); a segment figure is never compared. Only an average
  // or a share reaches this one.
  if (isTotalMetric(metricId) || MIRA_SEGMENT_METRICS.includes(metricId)) {
    return null;
  }
  const { references, segments } = splitReference(entities);
  // Above the name limit the levels replace the named bars, whatever order the caller keeps.
  if (comparisonUsesLevels(segments.length)) {
    return buildLevelBars(metricId, grain, referenceTrends, entities, formatValue, ctx, entityColors);
  }

  const base = buildTrend(referenceTrends, metricId, grain, formatValue, ctx);
  const points = scaffold(base.points);

  // Rank by the number of cases over the whole grain, descending (ties by label): the most-counted
  // companies lead.
  const ranked = keepOrder
    ? segments
    : [...segments].sort((a, b) => {
        const casesA = entityCasesOverGrain(a, grain);
        const casesB = entityCasesOverGrain(b, grain);
        return casesB - casesA || a.label.localeCompare(b.label);
      });

  const top = ranked.slice(0, MIRA_COMPARISON_NAME_LIMIT);
  const bars = [...references, ...top].map((entity) =>
    averageBars(entity, points, grain, entityColor(entity.id, entityColors, ctx.dark))
  );
  const belowBarFloor = bars.some((bar) => bar.hidden);
  const series: MiraStackedSeries[] = bars.map((bar) => bar.series);

  return {
    points,
    series,
    hasData: series.some((part) => part.values.some((value) => value !== null)),
    restCount: Math.max(0, ranked.length - top.length),
    belowBarFloor,
  };
}

/** Whether a figure may be stacked at all: a total, or one of the two segment figures. */
export function isStackableMetric(metricId: string): boolean {
  return MIRA_TOTAL_METRICS.includes(metricId) || MIRA_SEGMENT_METRICS.includes(metricId);
}
